import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, FileText } from 'lucide-react';

import { useCmsNavigation } from '@/shared/hooks/useCmsNavigation';
import { useLocale } from '@/shared/hooks/useLocale';

export function CmsPagesMenu() {
  const { data } = useCmsNavigation();
  const locale = useLocale();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const pages = data?.filter(page => page.header) ?? [];

  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => {
    if (!open) return;
    const close = (event: PointerEvent) => { if (!root.current?.contains(event.target as Node)) setOpen(false); };
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, [open]);

  if (!pages.length) return null;
  return <div ref={root} className="relative shrink-0" onBlur={event => {
    if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
  }} onKeyDown={event => {
    if (event.key === 'Escape') { setOpen(false); button.current?.focus(); }
  }}>
    <button ref={button} type="button" aria-expanded={open} aria-controls="cms-pages-links" onClick={() => setOpen(value => !value)} className="flex min-h-10 items-center gap-1.5 rounded-xl px-2 text-xs font-semibold text-fg-primary hover:bg-bg-muted focus-visible:outline focus-visible:outline-accent sm:text-sm">
      <FileText className="h-4 w-4" aria-hidden /><span className="sr-only sm:not-sr-only">{locale === 'ge' ? 'გვერდები' : 'Pages'}</span><ChevronDown className="hidden h-3 w-3 sm:block" aria-hidden />
    </button>
    {open && <nav id="cms-pages-links" aria-label={locale === 'ge' ? 'გვერდები' : 'Pages'} className="absolute left-0 top-full z-50 mt-2 max-h-96 w-64 max-w-[80vw] overflow-auto rounded-2xl border border-border-subtle bg-bg-canvas p-2 shadow-xl">
      {pages.map(page => <Link key={page.key} to={page.url} onClick={() => setOpen(false)} aria-current={pathname === page.url ? 'page' : undefined} className="block break-words rounded-xl px-4 py-3 text-sm text-fg-primary hover:bg-bg-muted focus-visible:outline focus-visible:outline-accent">{page.title}</Link>)}
    </nav>}
  </div>;
}
